import React, { useState, useEffect } from "react";
import { Bell, BellOff } from "lucide-react";
import {
  isMaintenanceAudioMuted,
  setMaintenanceAudioMuted,
  previewChimes,
} from "../utils/maintenanceAudio";

/**
 * v6.34.4 — Maintenance Tab audio toggle pill.
 *
 * Renders next to <HealthCheckIndicator /> in the Maintenance tab
 * header. Click toggles the localStorage "mosaic_maintenance_audio_muted"
 * key. When un-muting, the operator hears a preview chime sequence
 * (success → failure) so they can sanity-check volume / output
 * device BEFORE the next long-running action completes.
 */
export default function MaintenanceAudioToggle() {
  const [muted, setMuted] = useState(() => isMaintenanceAudioMuted());

  // Re-sync from localStorage when another tab toggles the preference.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "mosaic_maintenance_audio_muted") {
        setMuted(isMaintenanceAudioMuted());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggle = async () => {
    const next = !muted;
    setMaintenanceAudioMuted(next);
    setMuted(next);
    if (!next) {
      // Operator just turned audio ON — play a preview sequence so
      // they hear what the two chimes sound like and can verify the
      // volume / output device is what they expect.
      try {
        await previewChimes();
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      title={
        muted
          ? "Maintenance audio is MUTED — click to enable completion chimes"
          : "Maintenance audio is ON — click to mute"
      }
      data-testid="maintenance-audio-toggle"
      data-muted={muted ? "true" : "false"}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
        muted
          ? "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
          : "bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
      }`}
    >
      {muted ? <BellOff size={14} /> : <Bell size={14} />}
      <span>{muted ? "Audio off" : "Audio on"}</span>
    </button>
  );
}
